import { faSyringe } from '@fortawesome/pro-light-svg-icons';
import { Option } from '@spike/model';

import { FunctionComponent, useState } from 'react';
import EditSetting from './EditSetting';
import { useDispatch, useSelector } from 'react-redux';
import { useApiClientWrapper } from 'hooks';
import { CustomContentNotification } from '@spike/customcontentnotification-model';
import {
    CustomContentNotificationState,
    CustomContentNotificationStatus,
    updateOrCreateCustomContentNotification
} from '@spike/custom-content-notification-action';
import { RootState } from 'store';
import useNonInitialEffect from '@versiondos/hooks';
import { OverFullWindow, Spinner } from 'components/UI';
import EditNotificationContent from './EditCustomContentNotification/EditNotificationContent';

export interface Props {
    onEdit?: (emailChecked: boolean, smsChecked: boolean, noticeSelected: Option<string>, optionId: string) => void;
    loading: boolean;
}

export const EditExpirationVaccine: FunctionComponent<Props> = props => {
    const key = 'client_expiration_vaccine';
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const [editContentKey, setEditContentKey] = useState<string>('');
    const [customContentNotification, setCustomContentNotification] = useState<CustomContentNotification>({
        key: '',
        content: '',
        name: '',
        marketplaceID: 0
    });
    const [loading, setLoading] = useState(false);

    const customContentNotificationState = useSelector<RootState, CustomContentNotificationState>(
        state => state.customContentNotifications
    );

    const noticeOptions = [
        { id: '2', name: '2 hrs before' },
        { id: '4', name: '4 hrs before' },
        { id: '8', name: '8 hrs before' },
        { id: '12', name: '12 hrs before' },
        { id: '24', name: '1 day before' },
        { id: '48', name: '2 days before' },
        { id: '72', name: '3 days before' }
    ];

    const handleEdit = (emailChecked: boolean, smsChecked: boolean, noticeSelected: Option<string>) => {
        props.onEdit && props.onEdit(emailChecked, smsChecked, noticeSelected, key);
    };

    const handleSave = () => {
        setLoading(true);
        setEditContentKey('');
        dispatch(updateOrCreateCustomContentNotification(apiClientWrapper, customContentNotification));
    };

    useNonInitialEffect(() => {
        if (
            customContentNotificationState.status === CustomContentNotificationStatus.UpdateSuccess &&
            customContentNotificationState.customContentNotification
        ) {
            setCustomContentNotification(customContentNotificationState.customContentNotification);
            setLoading(false);
        }
    }, [customContentNotificationState.status]);

    const subtitle = <>Notifies clients when a pet&apos;s vaccines are due or have expired.</>;

    return editContentKey !== '' ? (
        <OverFullWindow
            hideHeader={false}
            showFooter={true}
            title={editContentKey.includes('email') ? 'Edit Email' : 'Preview SMS'}
            centered={true}
            onClose={() => setEditContentKey('')}
            onBack={() => setEditContentKey('')}
            onSave={() => handleSave()}
        >
            <EditNotificationContent
                notificationKey={editContentKey}
                title={'Expiration Vaccine'}
                subtitle={"Notifies clients when a pet's vaccines are due or have expired."}
                headerIcon={faSyringe}
                sectionSubtitle={"Customize vaccine notifications to keep pet parents on top of their pet's health."}
                customContentNotification={customContentNotification}
                setCustomContentNotification={setCustomContentNotification}
            />
        </OverFullWindow>
    ) : loading ? (
        <Spinner />
    ) : (
        <EditSetting
            optionId={key}
            noticeOptions={noticeOptions}
            headerIcon={faSyringe}
            title={'Expiration Vaccine'}
            subtitle={subtitle}
            loading={props.loading}
            defaultOption={noticeOptions[4]}
            onEdit={handleEdit}
            noShowOption={true}
            showEditContent={true}
            onEditContent={setEditContentKey}
        />
    );
};

export default EditExpirationVaccine;
